import React, { useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  ClickAwayListener,
  Container,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ChevronDown, Menu as MenuIcon, Search, ShoppingCart, Store, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { brand, categoryMenu } from "../data/estoreContent";
import { ESTORE_ROUTES, estoreShopUrl } from "../data/estorePaths";
import { useEstoreCart } from "../hooks/useEstoreCart";

function EstoreNavbar({
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  showSuggestions,
  searchSuggestions,
  onSuggestionClick,
  onClickAwaySuggestions,
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { count: cartCount } = useEstoreCart();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [sticky, setSticky] = useState(false);
  const [catAnchor, setCatAnchor] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  React.useEffect(() => {
    const onScroll = () => setSticky(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openCategories = Boolean(catAnchor);
  const openMobile = Boolean(mobileMenuAnchor);

  return (
    <AppBar
      position={sticky ? "fixed" : "static"}
      elevation={0}
      sx={{
        bgcolor: "rgba(255,255,255,0.92)",
        backdropFilter: "blur(12px)",
        borderBottom: `1px solid ${brand.stroke}`,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1, justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ cursor: "pointer" }} onClick={() => navigate(ESTORE_ROUTES.home)}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                background: `linear-gradient(135deg, ${brand.accent} 0%, ${brand.accent2} 100%)`,
              }}
            >
              <Store size={22} color="#fff" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.1, color: brand.text }}>
                {brand.name}
              </Typography>
              <Typography variant="caption" sx={{ color: brand.muted }}>
                {brand.tagline}
              </Typography>
            </Box>
          </Stack>

          {!isMobile && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                endIcon={<ChevronDown size={16} />}
                onClick={(e) => setCatAnchor(e.currentTarget)}
                sx={{
                  color: brand.text,
                  fontWeight: 700,
                  textTransform: "none",
                  borderRadius: 2,
                }}
              >
                Categories
              </Button>
              <Menu
                anchorEl={catAnchor}
                open={openCategories}
                onClose={() => setCatAnchor(null)}
                PaperProps={{ sx: { borderRadius: 2, minWidth: 220, mt: 1, border: `1px solid ${brand.stroke}` } }}
              >
                {categoryMenu.map((group) => (
                  <React.Fragment key={group.label}>
                    <Typography variant="overline" sx={{ px: 2, pt: 1.5, color: brand.muted, display: "block" }}>
                      {group.label}
                    </Typography>
                    {group.items.map((item) => (
                      <MenuItem
                        key={item}
                        onClick={() => {
                          setCatAnchor(null);
                          navigate(estoreShopUrl(item));
                        }}
                        sx={{ fontWeight: 600 }}
                      >
                        {item}
                      </MenuItem>
                    ))}
                  </React.Fragment>
                ))}
              </Menu>
              <Button onClick={() => navigate(ESTORE_ROUTES.shop)} sx={{ color: brand.text, fontWeight: 700, textTransform: "none" }}>
                Shop
              </Button>
            </Stack>
          )}

          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, justifyContent: "flex-end" }}>
            <ClickAwayListener onClickAway={onClickAwaySuggestions}>
              <Box sx={{ position: "relative", maxWidth: isMobile ? 200 : 320, width: "100%" }}>
                <TextField
                  size="small"
                  placeholder="Search products…"
                  value={searchQuery}
                  onChange={onSearchChange}
                  onKeyDown={(e) => e.key === "Enter" && onSearchSubmit()}
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 999,
                      bgcolor: "#fff",
                      border: `1px solid ${brand.stroke}`,
                      "& fieldset": { border: "none" },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <IconButton size="small" onClick={onSearchSubmit} aria-label="search">
                        <Search size={18} />
                      </IconButton>
                    ),
                  }}
                />
                {showSuggestions && searchSuggestions.length > 0 && (
                  <Paper
                    sx={{
                      position: "absolute",
                      top: "calc(100% + 6px)",
                      left: 0,
                      right: 0,
                      zIndex: 2000,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: `1px solid ${brand.stroke}`,
                    }}
                  >
                    <List dense>
                      {searchSuggestions.map((p) => (
                        <ListItemButton
                          key={p.id}
                          onClick={() => onSuggestionClick(p)}
                          sx={{ "&:hover": { bgcolor: "action.hover" } }}
                        >
                          <Avatar variant="rounded" src={p.image} sx={{ width: 40, height: 40, mr: 1.5 }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap>
                              {p.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {p.category} · ₹{p.price}
                            </Typography>
                          </Box>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </ClickAwayListener>

            <IconButton onClick={() => navigate(ESTORE_ROUTES.cart)} sx={{ color: brand.text }} aria-label="cart">
              <Badge badgeContent={cartCount} color="primary" max={99} invisible={cartCount === 0}>
                <ShoppingCart size={22} />
              </Badge>
            </IconButton>
            <IconButton onClick={() => navigate(ESTORE_ROUTES.account)} sx={{ color: brand.text }} aria-label="account">
              <User size={22} />
            </IconButton>

            {isMobile && (
              <>
                <IconButton onClick={(e) => setMobileMenuAnchor(e.currentTarget)} sx={{ color: brand.text }}>
                  <MenuIcon />
                </IconButton>
                <Menu
                  anchorEl={mobileMenuAnchor}
                  open={openMobile}
                  onClose={() => setMobileMenuAnchor(null)}
                  PaperProps={{ sx: { borderRadius: 2, minWidth: 200 } }}
                >
                  <MenuItem
                    onClick={() => {
                      setMobileMenuAnchor(null);
                      navigate(ESTORE_ROUTES.shop);
                    }}
                  >
                    Shop
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setMobileMenuAnchor(null);
                      navigate(ESTORE_ROUTES.home);
                    }}
                  >
                    Home
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setMobileMenuAnchor(null);
                      setCatAnchor(null);
                      navigate(ESTORE_ROUTES.shop);
                    }}
                  >
                    All categories
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
}

export default EstoreNavbar;
